/**
 * BNPL billing customer helpers (ERS-5, split out ERS-78)
 *
 * `buildBillingPhone` and `readBirthdayIso` moved here from `hooks/useBnplCheckoutScreen.ts` so
 * both the data screen (08, prefill) and the payment screen (09, provider payload) can import them
 * without one hook depending on the other.
 */

import type { RbiCoursePaymentBillingCustomer, SignedUpPatient } from '@physiofit-digital/physiofit_types';

/** `+{countryCode}{nationalNumber}` fallback when `patientPhone` has no leading `+`. */
export function buildBillingPhone(patient: SignedUpPatient): string {
  if (patient.patientPhone?.trim().startsWith('+')) {
    return patient.patientPhone.trim();
  }
  const cc = patient.patientPhoneCountryCode?.replace(/^\+/, '') ?? '';
  const nn = patient.patientPhoneNationalNumber ?? '';
  return `+${cc}${nn}`;
}

/** Normalizes a stored birthday to `YYYY-MM-DD`; a value that does not start with a date reads as `''`. */
export function readBirthdayIso(value: string | undefined): string {
  return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
}

const ADULT_AGE_YEARS = 18;
// A typo guard, not an age rule: minors are handled by `isMinorBirthday` after the save.
const MIN_PLAUSIBLE_AGE_YEARS = 6;

/** The day `years` before `today` on the browser's calendar, as `YYYY-MM-DD`. */
function yearsBefore(today: Date, years: number): string {
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear() - years}-${month}-${day}`;
}

/**
 * Under 18 today, on the browser's calendar.
 *
 * @param {string} birthdayIso Birth date from `BirthdayInput`, `YYYY-MM-DD`.
 * @param {Date} today Reference day.
 * @return {boolean} True when the patient is under 18.
 */
export function isMinorBirthday(birthdayIso: string, today: Date = new Date()): boolean {
  return birthdayIso > yearsBefore(today, ADULT_AGE_YEARS);
}

/**
 * A date that exists and lies from 1900 up to six years ago, on the same calendar as `isMinorBirthday`.
 *
 * @param {string} birthdayIso Birth date as typed in `BirthdayInput`, `YYYY-MM-DD` or `''`.
 * @param {Date} today Reference day.
 * @return {boolean} True when the date is plausible.
 */
export function isPlausibleBirthday(birthdayIso: string, today: Date = new Date()): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthdayIso);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const exists = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return exists && birthdayIso >= '1900-01-01' && birthdayIso <= yearsBefore(today, MIN_PLAUSIBLE_AGE_YEARS);
}

/** True once 08 has written every field the provider payload needs. */
export function hasCompletePrivateInfo(patient: SignedUpPatient): boolean {
  const info = patient.webAppOnboarding?.privateInfo;
  return Boolean(
    info?.firstName?.trim() &&
    info.lastName?.trim() &&
    info.email?.trim() &&
    readBirthdayIso(info.birthday) &&
    info.address?.street?.trim() &&
    info.address.zip?.trim() &&
    info.address.city?.trim(),
  );
}

/** The provider payload, built from what 08 persisted; call only after `hasCompletePrivateInfo`. */
export function buildBillingCustomerFromPatient(patient: SignedUpPatient): RbiCoursePaymentBillingCustomer {
  const info = patient.webAppOnboarding?.privateInfo;
  return {
    firstname: info?.firstName?.trim() ?? '',
    lastname: info?.lastName?.trim() ?? '',
    email: info?.email?.trim() ?? '',
    phone: buildBillingPhone(patient),
    birthdate: readBirthdayIso(info?.birthday),
    street: info?.address?.street ?? '',
    houseNumber: info?.address?.houseNumber ?? '',
    zip: info?.address?.zip?.trim() ?? '',
    city: info?.address?.city?.trim() ?? '',
    country: 'DE',
    language: 'de',
  };
}
